import {MODELS} from "../services/models";
import dates from "../services/dates";
import utils from "../services/utils";
import {ENUMS} from "../services/enums";
import {timeFrames} from "../services/staticData";
import router from "../router";
import store from "../store";

const state = () => ({
  columns: {
    contacts: {
      fullName: {
        class: MODELS.Contact.class_name,
        label: "Name",
        field: "full_name",
        sortable: true,
        visible: true,
        sticky: true,
        display(row, vue) {
          return `<span class="text-clickable">${row.full_name}</span>`;
        },
        onClick(row, vue) {
          router.replace({query: {contact: row.id}});
        },
      },
      phone1: {
        class: MODELS.Contact.class_name,
        label: "Primary Phone",
        field: "phone_1",
        sortable: true,
        visible: true,
        isSensitiveDate: true,
      },
      phone2: {
        class: MODELS.Contact.class_name,
        label: "Phone 2",
        field: "phone_2",
        sortable: true,
        visible: false,
        isSensitiveDate: true,
      },
      emailAddress: {
        class: MODELS.Contact.class_name,
        label: "Email",
        field: "email_address",
        sortable: true,
        visible: true,
        isSensitiveDate: true,
      },
      street: {
        class: MODELS.Contact.class_name,
        label: "Address",
        field: "street",
        sortable: true,
        visible: false,
      },
      types: {
        class: MODELS.Contact.class_name,
        label: "Types",
        field: "types",
        visible: true,
        display(row) {
          if (row.contactContactTypes.length) {
            let typeString = "";
            row.contactContactTypes.forEach(type => {
              const contactType = store.getters.contactTypes.find(contactType => contactType.id === type.contact_type_id)
              typeString += contactType.name + ", "
            });

            return typeString.trim().slice(0, -1);
          }

          return '--'
        }
      },
      tags: {
        class: MODELS.Contact.class_name,
        label: "Tags",
        field: "tags",
        visible: true,
        display(row) {
          let filteredTags = row.tags.filter((tag, index) => {
            if (index < 3) return tag.label;
          });

          return filteredTags.map(tag => tag.label).toString().replace(/,/g, ", ");
        }
      },
      leadCount: {
        class: MODELS.Contact.class_name,
        label: "Lead Count",
        field: "records_count",
        isNumeric: true,
        sortable: true,
        visible: true,
      },
    },
    preset: {
      fullName: {
        class: MODELS.Contact.class_name,
        label: "Name",
        field: "full_name",
        visible: true,
      },
      phone1: {
        class: MODELS.Contact.class_name,
        label: "Primary Phone",
        field: "phone_1",
        visible: true,
      },
      phone2: {
        class: MODELS.Contact.class_name,
        label: "Phone 2",
        field: "phone_2",
        visible: false,
      },
      emailAddress: {
        class: MODELS.Contact.class_name,
        label: "Email",
        field: "email_address",
        visible: true,
      },
      street: {
        class: MODELS.Contact.class_name,
        label: "Address",
        field: "street",
        visible: false,
      },
      types: {
        class: MODELS.Contact.class_name,
        label: "Types",
        field: "types",
        visible: true,
      },
      tags: {
        class: MODELS.Contact.class_name,
        label: "Tags",
        field: "tags",
        visible: true,
      },
      leadCount: {
        class: MODELS.Contact.class_name,
        label: "Lead Count",
        field: "records_count",
        visible: true,
      },
    },
  },
  filters: {
    contacts: {
      search: null,
      type: null,
      tag: null,
      city: null,
      state: null,
      postcode: null,
      includeSeller: false,
      timeFrames: null,
      timeFrameValues: [],
    },
    preset: {
      search: null,
      type: null,
      tag: null,
      city: null,
      state: null,
      postcode: null,
      includeSeller: false,
      timeFrames: null,
      timeFrameValues: [],
    },
  },
});

const getters = {
  columns: (state) => (columnObject) => {
    return state.columns[columnObject]
  },
  filters: (state) => (filterObject) => {
    return state.filters[filterObject]
  },
  filterCount(state, getters) {
    let count = 0
    for (let filterKey in getters.filters('contacts')) {
      const filterValue = getters.filters('contacts')[filterKey]

      if (filterKey !== 'timeFrameValues') {
        if (filterKey === 'timeFrames' && typeof filterValue === "string") {
          count += 1
        }

        if (filterKey !== 'timeFrames' && filterValue !== null) {
          count += 1
        }
      }
    }

    return count
  },
};

const mutations = {
  addCustomFieldsToColumns(state, [columnObject, properties]) {
    const objects= Object.fromEntries(properties.map(item => [item.key, item.value]));
    state.columns[columnObject] = Object.assign({}, state.columns[columnObject], objects)
  },
  setOneColumn(state, [columnObject, columnKey, value]) {
    state.columns[columnObject][columnKey].visible = value
  },
  setOneFilter(state, [filterObject, filter, value, customValue = null]) {
    state.filters[filterObject][filter] = value

    if (filter === 'timeFrames') {
      const timeFrameValue = timeFrames.find(t => t.value === state.filters[filterObject][filter])
      if (value === 'custom' && customValue && !timeFrameValue) {
        state.filters[filterObject].timeFrameValues = {
          start: customValue.start,
          end: customValue.end
        }
      }

      if (timeFrameValue) {
        state.filters[filterObject].timeFrameValues = {
          start: timeFrameValue.start,
          end: timeFrameValue.end,
        }
      }
    }
  },
  resetColumns(state, columnObject) {
    for (let columnsKey in state.columns[columnObject]) {
      state.columns[columnObject][columnsKey].visible = (!(columnsKey === 'phone2' || columnsKey === 'street'))
    }
  },
  resetFilters(state, filterObject) {
    // Make sure to update state default
    state.filters[filterObject] = {
      search: null,
      type: null,
      tag: null,
      city: null,
      state: null,
      postcode: null,
      includeSeller: false,
      timeFrames: null,
      timeFrameValues: [],
    }
  }
};

const actions = {
  setup({commit, dispatch, state, getters, rootGetters}, columnObject) {
    dispatch('loadCustomFieldsColumns', columnObject)

    const defaultPreset = rootGetters.contactsTableConfigurations.find(preset => preset.id === rootGetters.authUser.default_contacts_table_preset_id)
    if (! defaultPreset) {
      commit("resetColumns", "contacts")
      commit("resetFilters", "contacts")
      return
    }

    for (const [columnKey, columnValue] of Object.entries(getters.columns("contacts"))) {
      const columnPreset = defaultPreset.columns.find(preset => preset.field === columnValue.field)
      if (columnPreset) {
        commit("setOneColumn", ["contacts", columnKey, columnPreset.visible])
      }
    }

    for (const [filterKey, filterValue] of Object.entries(getters.filters("contacts"))) {
      const filterPresetValue = defaultPreset.filters[filterKey]
      if (filterPresetValue) {
        commit("setOneFilter", ["contacts", filterKey, filterPresetValue])
      }
    }
  },
  loadCustomFieldsColumns({commit, state, rootGetters}, columnObject) {
    let customFieldColumns = rootGetters.contactCustomFields.map(customField => {
      if (columnObject === 'preset') {
        return {
          key: utils.camelCase(customField.label),
          value: {
            class: customField.class_name,
            label: customField.label,
            field: customField.import_label,
            visible: true,
          }
        }
      }

      return {
        key: utils.camelCase(customField.label),
        value: {
          class: customField.class_name,
          label: customField.label,
          field: customField.import_label,
          visible: true,
          display(row) {
            if (row.custom_fields.length === 0) return '--'

            const field = row.custom_fields.find((cf) => cf.id === customField.id)
            if (!field.value) return '--'

            if (field.type === ENUMS.CustomFieldTypeEnum.Date.value) return dates.formatAsDate(field.value)

            if (field.type === ENUMS.CustomFieldTypeEnum.Currency.value) return utils.formatCurrency(field.value)

            if (field.type === ENUMS.CustomFieldTypeEnum.Time.value) return dates.formatAsTime(field.value)

            if (field.type === ENUMS.CustomFieldTypeEnum.TextArea.value) return utils.truncateString(field.value, 140)

            return field.value
          }
        }
      }
    })

    // this is here, so it will be always the last column
    if (columnObject === 'preset') {
      customFieldColumns.push({
        key: 'createdAt',
        value: {
          class: MODELS.Contact.class_name,
          label: 'Created At',
          field: 'created_at',
          visible: true,
        },
      })
    } else {
      customFieldColumns.push({
        key: 'createdAt',
        value: {
          class: MODELS.Contact.class_name,
          label: 'Created At',
          field: 'created_at',
          sortable: true,
          visible: true,
          display(row) {
            return dates.formatAsDate(row.created_at)
          }
        },
      })
    }

    commit("addCustomFieldsToColumns", [columnObject, customFieldColumns])
  },
};

const ContactsStore = {
  namespaced: true,
  state,
  getters,
  mutations,
  actions,
};

export default ContactsStore;
